import * as React from "react";
import { Link } from "react-router";

import { useCurrentOrg } from "@/providers/auth/hooks";
import { actionDetailPath } from "@/shared/components/actions/model";
import { AgentLink } from "@/shared/components/links/AgentLink";
import { SlackChannelLink } from "@/shared/components/links/SlackChannelLink";
import { FormattedDateTime } from "@/shared/datetime";
import {
  conditionClauses,
  summarizeTrigger,
  targetActionReference,
  TriggerTargetType,
  type TriggerClause,
  type TriggerOutcome,
  type TriggerRecord,
} from "@/shared/domain/triggers";

import styles from "./TriggerSentence.module.css";

/**
 * What the trigger does, one clause per row.
 *
 * This is the page's thesis, not decoration: a reader arriving at a trigger
 * wants to know whether it does the right thing. The rows are the sentence
 * "when X and Y, then Z" broken at its joints, so a trigger with four
 * conditions and three targets is still something a person can check line by
 * line instead of parsing a paragraph. The connector word leads each row, so
 * the shape of the rule is readable down the left edge alone.
 *
 * It carries no frame of its own -- the read view and the form's preview card
 * each supply their own -- so it never draws a border inside a border.
 *
 * The prose comes from `summarizeTrigger` and `conditionClauses`, both tested
 * on their own; this component lays it out and links the nouns.
 */
export function TriggerSentence({ record }: { record: TriggerRecord }) {
  const summary = summarizeTrigger(record);
  const clauses = conditionClauses(record.condition);

  return (
    <ul
      className={styles.rows}
      aria-label="What this trigger does"
      data-testid="trigger-sentence"
    >
      <Row keyword="When" kind="firing">
        {summary.when}
      </Row>
      {clauses.map((clause, index) => (
        <Row key={index} keyword="And" kind="firing">
          <ClauseText clause={clause} />
        </Row>
      ))}
      {/* After the conditions, because that is the order the engine applies
          them in: an occurrence that fails a condition does not use up its
          subject's one firing. */}
      {summary.grain !== null && (
        <Row
          keyword={summary.grain.keyword}
          kind="firing"
          testId="trigger-sentence-grain"
        >
          {summary.grain.clause}
        </Row>
      )}
      {summary.outcomes.map((outcome, index) => (
        <Row
          key={outcome.target.target_id}
          keyword={index === 0 ? "Then" : "And"}
          kind="outcome"
        >
          <OutcomeClause outcome={outcome} triggerOrgId={record.org_id} />
        </Row>
      ))}
      {summary.outcomes.length === 0 && (
        <Row keyword="Then" kind="outcome">
          <span className={styles.nothing}>
            nothing happens &mdash; this trigger has no targets.
          </span>
        </Row>
      )}
    </ul>
  );
}

/**
 * One row: the connector word, then the clause. `kind` tints the outcome half
 * so the rule's cause and its effect are told apart without reading them.
 */
function Row({
  keyword,
  kind,
  testId,
  children,
}: {
  keyword: string;
  kind: "firing" | "outcome";
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <li className={styles.row} data-row-kind={kind} data-testid={testId}>
      <span className={styles.keyword}>{keyword}</span>
      <div className={styles.clause}>{children}</div>
    </li>
  );
}

/**
 * A comparison's three parts weighted apart: the field leads, the comparator
 * recedes to grammar, and the value sits in a chip, since it is the one part
 * that is the author's own text rather than the namespace's.
 */
function ClauseText({ clause }: { clause: TriggerClause }) {
  if (clause.kind === "expression") {
    return <>{clause.text}</>;
  }
  return (
    <>
      <span className={styles.field}>{clause.field}</span>{" "}
      <span className={styles.comparator}>{clause.comparator}</span>
      {clause.value !== null && (
        <>
          {" "}
          {clause.value.kind === "instant" ? (
            // Written in UTC, read in the zone and format this person picked.
            <FormattedDateTime
              className={styles.value}
              value={clause.value.iso}
              timeZone
            />
          ) : (
            <code className={styles.value}>{clause.value.text}</code>
          )}
        </>
      )}
    </>
  );
}

/**
 * Every target this names opens in a new tab: the reader is checking a rule
 * they are in the middle of writing or auditing, and following a link should
 * not cost them the page they are checking.
 */
function OutcomeClause({
  outcome,
  triggerOrgId,
}: {
  outcome: TriggerOutcome;
  /** The trigger's org, which owns an action stored without an owner. */
  triggerOrgId: string;
}) {
  const viewerOrgId = useCurrentOrg()?.org_id;
  const { target } = outcome;
  return (
    <>
      {outcome.verb}{" "}
      {target.type === TriggerTargetType.InvokeAction && (
        <Link
          to={actionDetailPath(
            targetActionReference(target.action, triggerOrgId),
            viewerOrgId,
          )}
          className={styles.target}
          target="_blank"
          rel="noreferrer"
        >
          {target.action.name}
        </Link>
      )}
      {target.type === TriggerTargetType.StartAgent && (
        <span className={styles.target}>
          <AgentLink agentId={target.agent_id} newTab />
        </span>
      )}
      {target.type === TriggerTargetType.SendSlackMessage && (
        <span className={styles.target}>
          <SlackChannelLink channelId={target.channel_id} />
        </span>
      )}
    </>
  );
}
